import { Fragment } from 'react';

import type { ReportInline, ReportText as ReportTextData } from '@/entities/publicReport';
import { cn } from '@/shared/lib';

export interface ReportTextProps {
    text: ReportTextData;
    className?: string;
}

/**
 * Een rapporttekst zoals de PDF hem drukt (`ReportTextRenderer::html()`): een
 * alinea per blok met regeleinden erin, één opsomming per reeks bullets, en een
 * link die in een nieuw tabblad opent zonder de pagina mee te geven.
 *
 * Er komt geen html aan te pas: React zet elke tekst neer als tekst. Een link
 * wordt alleen een link als hij met `https://` begint; de API liet al alleen
 * veilige adressen door, dit is de tweede grendel.
 */
export function ReportText({ text, className }: ReportTextProps) {
    if (text.length === 0) {
        return null;
    }

    return (
        <div className={cn('flex flex-col gap-[10px]', className)}>
            {text.map((block, index) =>
                block.type === 'list' ? (
                    <ul key={index} className="m-0 flex list-disc flex-col gap-1 pl-5">
                        {block.items.map((item, itemIndex) => (
                            <li
                                key={itemIndex}
                                className="pl-[2px] text-[15px] leading-[1.6] text-pretty text-[var(--text-reading)] marker:text-[var(--text-tertiary-safe)]"
                            >
                                <Line nodes={item} />
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p
                        key={index}
                        className="m-0 text-[15px] leading-[1.6] text-pretty text-[var(--text-reading)]"
                    >
                        {block.lines.map((line, lineIndex) => (
                            <Fragment key={lineIndex}>
                                {lineIndex > 0 ? <br /> : null}
                                <Line nodes={line} />
                            </Fragment>
                        ))}
                    </p>
                ),
            )}
        </div>
    );
}

function Line({ nodes }: { nodes: ReportInline[] }) {
    return nodes.map((node, index) =>
        node.type === 'link' && isSafeHref(node.href) ? (
            <a
                key={index}
                href={node.href}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="break-words text-[var(--accent-dark)] underline underline-offset-2 hover:text-[var(--accent)]"
            >
                {node.text}
            </a>
        ) : (
            <Fragment key={index}>{node.text}</Fragment>
        ),
    );
}

/** Alleen een absoluut https-adres zonder tekens die uit een attribuut kunnen breken. */
function isSafeHref(href: string): boolean {
    return /^https:\/\/[^\s"'<>\\`]+$/i.test(href);
}
